import type { MotionEffectsReactions, IScrubReactionManager, TriggersAndReactionsPageConfig } from './types'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { MotionEffectsReactionsSymbol, name } from './symbols'
import { BrowserWindow, BrowserWindowSymbol, PageFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { BreakpointRange, ReactionsInBpRangeWithTriggerData, TriggerVariant } from '@wix/thunderbolt-becky-types'
import { isTriggerBpRangeInCurrentWindowRange } from './utils'
import { isSSR } from '@wix/thunderbolt-commons'

export const scrubReactionsManager = withDependencies(
	[named(PageFeatureConfigSymbol, name), BrowserWindowSymbol, MotionEffectsReactionsSymbol],
	(
		{ scrubEffectToTrigger }: TriggersAndReactionsPageConfig,
		browserWindow: NonNullable<BrowserWindow>,
		motionEffectsReactions: MotionEffectsReactions
	): IScrubReactionManager => {
		let activeListeners: Array<{
			mediaQueryList: MediaQueryList
			mediaQueryHandler: (event: MediaQueryListEvent) => void
		}> = []
		const { scrubReactionWithBpRanges, reactionsBpRanges } = scrubEffectToTrigger

		const stopAnimationsIfNeeded = () => {
			const noneMatched = activeListeners.every((activeListener) => !activeListener.mediaQueryList.matches)
			if (noneMatched) {
				motionEffectsReactions.scrub({})
			}
		}

		const handleMediaQuery = (event: MediaQueryListEvent) => {
			if (event.matches) {
				motionEffectsReactions.scrub(getEffectsCurrentBreakpoint(scrubReactionWithBpRanges))
			} else {
				stopAnimationsIfNeeded()
			}
		}

		const getEffectsCurrentBreakpoint = (scrubReactionWithRanges: Array<ReactionsInBpRangeWithTriggerData>) => {
			return scrubReactionWithRanges.reduce((acc, reactionData) => {
				const isInBreakpoint = isTriggerBpRangeInCurrentWindowRange(reactionData.triggerBpRange)
				if (isInBreakpoint) {
					reactionData.reactions.forEach((reaction) => {
						acc[reaction.reactionData.effect] = reactionData.triggerData
					})
				}
				return acc
			}, {} as Record<string, TriggerVariant>)
		}

		const getMatchMediaString = (range: BreakpointRange): string => {
			const maxString = range.max ? `(max-width:${range!.max}px)` : ''
			const minString = range.min ? `(min-width:${range!.min}px)` : ''
			return maxString && minString ? `${maxString} and ${minString}` : `${maxString}${minString}`
		}

		const observeBreakpointChange = (reactionsBpRangesToObserve: Array<BreakpointRange>) => {
			reactionsBpRangesToObserve.forEach((range) => {
				const matchMediaString = getMatchMediaString(range)
				const mediaQueryList = browserWindow.matchMedia(matchMediaString)
				activeListeners.push({ mediaQueryList, mediaQueryHandler: handleMediaQuery })
				mediaQueryList.addEventListener('change', handleMediaQuery)
			})
		}
		const init = () => {
			if (isSSR(browserWindow)) {
				return
			}
			const isOnlyDefaultRange = reactionsBpRanges[0]?.min && reactionsBpRanges[0]?.max

			if (reactionsBpRanges.length > 1 && !isOnlyDefaultRange) {
				observeBreakpointChange(reactionsBpRanges)
			}

			motionEffectsReactions.scrub(getEffectsCurrentBreakpoint(scrubReactionWithBpRanges))
		}

		const destroy = () => {
			if (isSSR(browserWindow)) {
				return
			}

			activeListeners.forEach(({ mediaQueryList, mediaQueryHandler }) => {
				mediaQueryList.removeEventListener('change', mediaQueryHandler)
			})

			activeListeners = []
		}

		return {
			init,
			destroy,
		}
	}
)
